import axios from "axios";
import {
  getTestUnavailableResponse,
  isTestFeatureUnavailableError,
} from "../utils/testEnvironment";

const MATCH_SCORE_API_BASE_URL =
  process.env.REACT_APP_MATCH_SCORE_API_BASE_URL ||
  "https://jobmatch.workbrook.com";

const MATCH_SCORE_FEATURE = "Job Match Score";

/**
 * External job match (paste URL / description) — used by Job Match Score page.
 */
export const postExternalMatchScore = async (payload) => {
  const blocked = getTestUnavailableResponse(MATCH_SCORE_FEATURE);
  if (blocked) {
    return blocked;
  }

  try {
    const response = await axios.post(
      `${MATCH_SCORE_API_BASE_URL}/api/v1/get-match-score-external`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return { success: true, data: response.data };
  } catch (error) {
    if (isTestFeatureUnavailableError(error)) {
      return getTestUnavailableResponse(MATCH_SCORE_FEATURE);
    }
    throw error;
  }
};

/**
 * Fetches match score from external API
 * @param {Object} userData - User data (must contain id field)
 * @param {Object} job - Job data (must contain id field)
 * @returns {Promise<Object>} Match score response with score, recommendation, and details
 */
export const getMatchScore = async (
  jobId,
  userId,
  includeCultureFit = false,
) => {
  const blocked = getTestUnavailableResponse(MATCH_SCORE_FEATURE);
  if (blocked) {
    return {
      ...blocked,
      matchScore: null,
      recommendation: null,
      details: null,
    };
  }

  try {
    if (!userId || !jobId) {
      throw new Error("User data and job data are required");
    }

    const requestBody = {
      user_id: userId,
      job_id: jobId,
      include_culture_fit: includeCultureFit,
    };

    const response = await axios.post(
      `${MATCH_SCORE_API_BASE_URL}/api/v1/get-match-score`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 1000000,
      },
    );

    return {
      success: true,
      matchScore: response.data.match_score || 0,
      recommendation: response.data.recommendation || "",
      details: response.data.details || {},
    };
  } catch (error) {
    console.error("Error fetching match score:", error);

    if (error.response) {
      return {
        success: false,
        error: error.response.data?.message || "Failed to fetch match score",
        matchScore: null,
        recommendation: null,
        details: null,
      };
    }
    if (error.request) {
      return {
        success: false,
        error: "Match score service is unavailable",
        matchScore: null,
        recommendation: null,
        details: null,
      };
    }
    return {
      success: false,
      error: error.message || "An error occurred",
      matchScore: null,
      recommendation: null,
      details: null,
    };
  }
};

/**
 * Analyzes a resume from a Cloudinary URL
 */
export const analyzeResume = async (resumeUrl) => {
  const blocked = getTestUnavailableResponse("Resume analysis");
  if (blocked) {
    return { ...blocked, data: null };
  }

  try {
    if (!resumeUrl) {
      throw new Error("Resume URL is required");
    }

    const response = await axios.post(
      `${MATCH_SCORE_API_BASE_URL}/api/v1/analyze-resume`,
      { resume_url: resumeUrl },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 3000000,
      },
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Error analyzing resume:", error);

    if (error.response) {
      return {
        success: false,
        error: error.response.data?.message || "Failed to analyze resume",
        data: null,
      };
    }
    if (error.request) {
      return {
        success: false,
        error: "Resume analysis service is unavailable",
        data: null,
      };
    }
    return {
      success: false,
      error: error.message || "An error occurred",
      data: null,
    };
  }
};

/**
 * Gets job recommendations based on user profile
 */
export const recommendJobs = async (payload) => {
  const blocked = getTestUnavailableResponse("Job recommendations");
  if (blocked) {
    return { ...blocked, data: null };
  }

  try {
    if (!payload) {
      throw new Error("User profile is required");
    }

    const response = await axios.post(
      `${MATCH_SCORE_API_BASE_URL}/api/v1/recommend-jobs`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        timeout: 3000000,
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching job recommendations:", error);

    if (error.response) {
      return {
        success: false,
        error:
          error.response.data?.description ||
          error.response.data?.message ||
          "Failed to fetch job recommendations",
        data: null,
      };
    }
    if (error.request) {
      return {
        success: false,
        error: "Job recommendation service is unavailable",
        data: null,
      };
    }
    return {
      success: false,
      error: error.message || "An error occurred",
      data: null,
    };
  }
};

export default getMatchScore;
